/**
 * Query Key Factory Pattern
 *
 * Centralized way to create consistent query keys across the application.
 * Each resource has its own key factory with hierarchical structure.
 */

export const queryKeys = {
  // Base key for all queries
  all: ["veraciti-vendor"] as const,

  // Auth
  auth: () => [...queryKeys.all, "auth"] as const,
  currentUser: () => [...queryKeys.auth(), "me"] as const,

  // Companies
  companies: () => [...queryKeys.all, "companies"] as const,
  company: (id: number) => [...queryKeys.companies(), id] as const,
  companyList: (filters?: any) => [...queryKeys.companies(), "list", filters] as const,

  // Tenant lifecycle
  tenants: () => [...queryKeys.all, "tenants"] as const,
  tenantStatus: (tenantId: number) => [...queryKeys.tenants(), tenantId, "status"] as const,

  // Company Types
  companyTypes: () => [...queryKeys.all, "companyTypes"] as const,
  companyTypeList: (filters?: any) => [...queryKeys.companyTypes(), "list", filters] as const,

  // Users
  users: () => [...queryKeys.all, "users"] as const,
  user: (id: number) => [...queryKeys.users(), id] as const,
  userList: (filters?: any) => [...queryKeys.users(), "list", filters] as const,

  // Roles
  roles: () => [...queryKeys.all, "roles"] as const,
  role: (id: number) => [...queryKeys.roles(), id] as const,
  roleList: (filters?: any) => [...queryKeys.roles(), "list", filters] as const,

  // Grants (Permissions)
  grants: () => [...queryKeys.all, "grants"] as const,
  grant: (id: number) => [...queryKeys.grants(), id] as const,
  grantList: (filters?: any) => [...queryKeys.grants(), "list", filters] as const,

  // Permissions
  permissions: () => [...queryKeys.all, "permissions"] as const,
  permissionList: (filters?: any) => [...queryKeys.permissions(), "list", filters] as const,

  // Branding
  branding: () => [...queryKeys.all, "branding"] as const,
  brandingDetail: (tenantId: number) => [...queryKeys.branding(), tenantId] as const,

  // Entitlements
  entitlements: () => [...queryKeys.all, "entitlements"] as const,
  entitlementsCatalog: (tenantId: number) => [...queryKeys.entitlements(), "catalog", tenantId] as const,

  // Billing
  billing: () => [...queryKeys.all, "billing"] as const,
  billingMetrics: (tenantId: number, filters?: any) => [...queryKeys.billing(), "metrics", tenantId, filters] as const,
  billingMetricsList: (filters?: any) => [...queryKeys.billing(), "metrics", "list", filters] as const,

  // Analytics
  analytics: () => [...queryKeys.all, "analytics"] as const,
  analyticsRevenueSummary: (params?: any) => [...queryKeys.analytics(), "revenue", "summary", params] as const,
  analyticsRevenueOverTime: (params?: any) => [...queryKeys.analytics(), "revenue", "over-time", params] as const,
  analyticsRevenueByCompany: (params?: any) => [...queryKeys.analytics(), "revenue", "by-company", params] as const,
  analyticsOperationsStatus: (params?: any) => [...queryKeys.analytics(), "operations", "status", params] as const,
  analyticsTopProducts: (params?: any) => [...queryKeys.analytics(), "products", "top", params] as const,
  analyticsNewTenantsOverTime: (params?: any) =>
    [...queryKeys.analytics(), "companies", "new-tenants", params] as const,

  // Software
  software: () => [...queryKeys.all, "software"] as const,
  softwareList: (filters?: any) => [...queryKeys.software(), "list", filters] as const,

  // Scopes
  scopes: () => [...queryKeys.all, "scopes"] as const,
  scopeList: (filters?: any) => [...queryKeys.scopes(), "list", filters] as const,

  // Pre-registrations
  preregistrations: () => [...queryKeys.all, "preregistrations"] as const,
  preregistration: (id: number) => [...queryKeys.preregistrations(), id] as const,
  preregistrationList: (filters?: any) => [...queryKeys.preregistrations(), "list", filters] as const,

  // External services
  external: () => [...queryKeys.all, "external"] as const,
  cep: (cep: string) => [...queryKeys.external(), "cep", cep] as const,
  cities: (stateCode: string) => [...queryKeys.external(), "cities", stateCode] as const,
} as const

// Type helper for query keys
export type QueryKeys = typeof queryKeys
